import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useProjectViewStore } from '@/stores/projectView';
import type { ProjectTask } from '@/types/odoo';
import { ProjectSidebar } from '@/components/project/ProjectSidebar';
import { OfferteTab } from '@/components/project/OfferteTab';
import { PlanningTab } from '@/components/project/PlanningTab';
import { MateriaalTab } from '@/components/project/MateriaalTab';
import { DocumentenTab } from '@/components/project/DocumentenTab';
import { InstallateurTaakTab } from '@/components/project/InstallateurTaakTab';
import { InkoopordersTab } from '@/components/project/InkoopordersTab';
import { DashboardTab } from '@/components/project/DashboardTab';
import { KoppelVerkoopsorderModal } from '@/components/project/KoppelVerkoopsorderModal';
import { usePermissionsStore } from '@/stores/permissions';
import type { TabName } from '@/stores/projectView';

const TM_SELECTIECODE = 'TM - Projecten materialen';
const TM_ALLOWED_TABS: TabName[] = ['materiaal', 'documenten'];

function findNearestFutureTask(tasks: ProjectTask[]): number | null {
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const future = tasks
    .filter(t => t.date_deadline && String(t.date_deadline).slice(0, 10) >= todayStr)
    .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
  return future[0]?.id ?? tasks[0]?.id ?? null;
}

const TAB_LABELS: Record<TabName, string> = {
  installateur_taak: 'Installateur Taak',
  offerte: 'Offerte',
  planning: 'Planning',
  materiaal: 'Materiaal',
  documenten: 'Documenten',
  inkooporders: 'Inkooporders',
  // 'dashboard' kept LAST so the "first permitted tab" fallback (below) never
  // auto-lands a user on the in-progress dashboard. Visual order lives in ProjectSidebar.
  dashboard: 'Dashboard',
};

function isTabName(value: string): value is TabName {
  return Object.prototype.hasOwnProperty.call(TAB_LABELS, value);
}

/** The first tab in TAB_LABELS order this user/project may see, or null (e.g. permissions not loaded). */
function firstPermittedTab(canShowTab: (tab: TabName) => boolean): TabName | null {
  return (Object.keys(TAB_LABELS) as TabName[]).find((t) => canShowTab(t)) ?? null;
}

/**
 * Where a `?tab=` deep link sends this user, or null when the URL decides nothing (no
 * param, not a tab, or a retired-tab redirect whose Dashboard is not shown here).
 * A tab the user may not see sends them to their first permitted tab — never to a
 * hidden one. While the URL decides, it owns the active tab: the "not permitted"
 * fallback effect yields to it, so the two can never overwrite each other.
 */
function urlTargetTab(rawTabParam: string | null, canShowTab: (tab: TabName) => boolean): TabName | null {
  if (!rawTabParam) return null;
  if (rawTabParam === 'collectoren' || (rawTabParam === 'offerte' && !canShowTab('offerte'))) {
    return canShowTab('dashboard') ? 'dashboard' : null;
  }
  if (!isTabName(rawTabParam)) return null;
  return canShowTab(rawTabParam) ? rawTabParam : firstPermittedTab(canShowTab);
}

export function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { detail, loading, error, activeTab, activeTaskId, fetchProject, setActiveTab, setActiveTask } =
    useProjectViewStore(useShallow((s) => ({
      detail: s.detail,
      loading: s.loading,
      error: s.error,
      activeTab: s.activeTab,
      activeTaskId: s.activeTaskId,
      fetchProject: s.fetchProject,
      setActiveTab: s.setActiveTab,
      setActiveTask: s.setActiveTask,
    })));
  const canTab = usePermissionsStore((s) => s.canTab);
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const [koppelModalOpen, setKoppelModalOpen] = useState(false);

  // TM projects only show materiaal + documenten tabs
  const isTmProject = detail?.saleOrder?.x_studio_selectiecode === TM_SELECTIECODE;
  const isEasykit = detail?.saleOrder?.x_studio_selectiecode === 'EK - Easykit';

  const canShowTab = useCallback(
    (tab: TabName): boolean => {
      if (!canTab(tab)) return false;
      if (isTmProject && !TM_ALLOWED_TABS.includes(tab)) return false;
      // Dashboard is only built for regular projects — keep it off EasyKit projects.
      if (isEasykit && tab === 'dashboard') return false;
      // Offerte now lives as a Dashboard section — hide the standalone pill wherever
      // the Dashboard is shown (regular project + dashboard-capable role). The pill
      // stays as the fallback on EasyKit projects (Dashboard guarded off above) and
      // for roles without the Dashboard tab (oplijster_easykit, warehouse).
      if (tab === 'offerte' && canTab('dashboard') && !isEasykit) return false;
      return true;
    },
    [canTab, isTmProject, isEasykit],
  );

  // The legacy "Collectoren" tab was RETIRED — its UI lives in the Dashboard's
  // Collectoren section now. A stale `?tab=collectoren` deep link (bookmarks, old
  // links) lands on the Dashboard and focuses that section when available; else it
  // falls through to the default landing behaviour (TM / EasyKit / no-dashboard).
  const [focusDashboardSection, setFocusDashboardSection] = useState<string | null>(null);
  // True while the user is on the Dashboard *via* the familiar "Collectoren"
  // shortcut — drives which of the paired sidebar entries lights up as active.
  const [collectorenActive, setCollectorenActive] = useState(false);
  // Installateur Taak now lives as the Dashboard's first section. Wherever the
  // Dashboard shows, the familiar pill / sidebar entry and a `?tab=installateur_taak`
  // link go straight to the Dashboard with that section focused (never through the
  // old tab, so a mounted Dashboard keeps its fase and open sections). EasyKit
  // projects and no-dashboard roles (warehouse) keep the standalone tab.
  const taakOnDashboard = canShowTab('dashboard');
  const goToTab = useCallback((tab: TabName) => {
    setCollectorenActive(false);
    if (tab === 'installateur_taak' && taakOnDashboard) {
      setActiveTab('dashboard');
      setFocusDashboardSection('installateur_taak');
      return;
    }
    setActiveTab(tab);
  }, [setActiveTab, taakOnDashboard]);
  // "Collectoren" shortcut → jump to the Dashboard and open its collectoren
  // section. Re-clickable: re-setting the focus section re-expands + re-scrolls.
  const goToCollectoren = useCallback(() => {
    setActiveTab('dashboard');
    setCollectorenActive(true);
    setFocusDashboardSection('collectoren');
  }, [setActiveTab]);

  // Read tab from URL query param (e.g. ?tab=inkooporders). The raw param is read
  // separately so the retired `collectoren` value (no longer a TabName) can still
  // be intercepted and redirected.
  const rawTabParam = searchParams.get('tab');
  const urlTab = rawTabParam as TabName | null;
  useEffect(() => {
    if (rawTabParam === 'collectoren') {
      if (canShowTab('dashboard')) {
        setActiveTab('dashboard');
        setCollectorenActive(true);
        setFocusDashboardSection('collectoren');
      }
      return;
    }
    // Offerte was retired into the Dashboard's Offerte section — a stale
    // `?tab=offerte` link lands on the Dashboard and focuses that section wherever
    // the pill is now hidden. On EK / no-dashboard roles the pill still exists, so
    // fall through to the normal `canShowTab('offerte')` handling below.
    if (rawTabParam === 'offerte' && !canShowTab('offerte') && canShowTab('dashboard')) {
      setActiveTab('dashboard');
      setFocusDashboardSection('offerte');
      return;
    }
    // A tab the user may see opens; one they may not see opens their first permitted tab.
    const target = urlTargetTab(rawTabParam, canShowTab);
    if (target) {
      goToTab(target);
    }
  }, [rawTabParam, canShowTab, goToTab, setActiveTab]);

  // Backstop for a tab set without goToTab (opening a project: the first-permitted
  // fallback below picks installateur_taak first, or a remembered tab): land on the
  // Dashboard at the top, without focusing the section. Only a click or link scrolls.
  useEffect(() => {
    if (activeTab === 'installateur_taak' && taakOnDashboard) setActiveTab('dashboard');
  }, [activeTab, taakOnDashboard, setActiveTab]);

  // Default landing tab: when entering a project WITHOUT an explicit ?tab= (e.g.
  // via search or a planning card), open the Dashboard if this user/project can
  // show it. Fires once per project load (ref guard) so later manual tab clicks
  // are preserved; deep links, non-dashboard roles and TM/EasyKit projects fall
  // through to the existing persisted/first-permitted behaviour.
  const defaultedProjectRef = useRef<number | null>(null);
  useEffect(() => {
    if (!detail) return;
    if (defaultedProjectRef.current === detail.projectId) return;
    defaultedProjectRef.current = detail.projectId;
    if (urlTab) return;
    if (canShowTab('dashboard')) {
      setActiveTab('dashboard');
    }
  }, [detail, urlTab, canShowTab, setActiveTab]);

  // Read task from URL on mount / URL change
  const urlTaskId = searchParams.get('task') ? Number(searchParams.get('task')) : null;

  // Refs to avoid setSearchParams in effect dependency arrays
  // (React Router does not guarantee stable identity for setSearchParams)
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Reveal the active pill in the horizontally-scrolling mobile tab strip when
  // it attaches, so a deep-linked tab that renders late in the strip (e.g.
  // ?tab=dashboard, which TAB_LABELS orders last) is never left off-screen
  // right. A callback ref is used rather than a [activeTab] effect because the
  // strip only renders once `detail` has loaded — which is AFTER the URL effect
  // settles activeTab — so the node attaches later than activeTab changes; the
  // callback fires exactly when the active pill mounts.
  const scrollActivePillIntoView = useCallback((node: HTMLButtonElement | null) => {
    node?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProject(Number(projectId));
    }
    // A new project resets the Collectoren-shortcut highlight (unless the URL
    // re-asserts it via ?tab=collectoren, handled in its own effect).
    setCollectorenActive(false);
  }, [projectId, fetchProject]);

  // Sync URL task param → store, or auto-select nearest future task
  useEffect(() => {
    if (!detail) return;
    if (urlTaskId) {
      // A ?task= link is a chosen task; skip the id already active (the auto-pick
      // below writing the URL, or a refresh) so it is not counted as a new choice.
      if (urlTaskId !== useProjectViewStore.getState().activeTaskId) setActiveTask(urlTaskId);
    } else {
      const nearest = findNearestFutureTask(detail.projectTasks);
      if (nearest) {
        setActiveTask(nearest, true);
        const next = new URLSearchParams(searchParamsRef.current);
        next.set('task', String(nearest));
        setSearchParamsRef.current(next, { replace: true });
      }
    }
  }, [detail, urlTaskId, setActiveTask]);

  // Sync store → URL when activeTaskId changes (e.g. from Planning tab click)
  useEffect(() => {
    if (!activeTaskId || !detail) return;
    const currentUrlTask = searchParamsRef.current.get('task') ? Number(searchParamsRef.current.get('task')) : null;
    if (currentUrlTask !== activeTaskId) {
      const next = new URLSearchParams(searchParamsRef.current);
      next.set('task', String(activeTaskId));
      setSearchParamsRef.current(next, { replace: true });
    }
  }, [activeTaskId, detail]);

  // If current active tab is not permitted, switch to first permitted tab — unless a
  // ?tab= deep link decides the tab: the URL effect above sets it in the same commit,
  // and this effect, still seeing the store's initial tab, would otherwise overwrite it.
  useEffect(() => {
    if (urlTargetTab(rawTabParam, canShowTab) !== null) return;
    const first = firstPermittedTab(canShowTab);
    if (first && !canShowTab(activeTab)) {
      setActiveTab(first);
    }
  }, [rawTabParam, activeTab, canShowTab, setActiveTab]);

  const handleRefresh = () => {
    if (projectId) {
      fetchProject(Number(projectId));
    }
  };

  return (
    <div>
      {/* Top bar */}
      <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-2 lg:pb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            if (window.history.state?.idx > 0) {
              navigate(-1);
            } else {
              navigate('/tasks');
            }
          }}
          className="flex items-center gap-1.5 text-sm font-semibold text-td-blue hover:text-td-navy transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Terug
        </button>
        <div className="flex items-center gap-2">
          {detail && (
            <button
              type="button"
              onClick={() => {
                window.open(`https://thermoduct.odoo.com/odoo/action-651/project.project/${detail.projectId}`, '_blank');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Bekijk in Odoo
            </button>
          )}
          {detail && !detail.sale_order_id && canFeature('koppel_verkoopsorder') && (
            <button
              type="button"
              onClick={() => setKoppelModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-td-navy bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
            >
              Koppel aan verkoopsorder
            </button>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-td-navy bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Vernieuwen
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && !detail && (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Project laden...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mx-4 lg:mx-8 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && !detail && (
        <div className="text-center py-12">
          <p className="text-td-muted text-sm">Project niet gevonden</p>
        </div>
      )}

      {detail && (
        <div className="px-4 lg:px-8 pb-8">
          {/* Mobile tab strip */}
          <div className="lg:hidden mb-4 -mx-4 px-4 overflow-x-auto">
            <nav aria-label="Project secties mobiel">
              <ul className="flex gap-1 min-w-max">
                {(Object.keys(TAB_LABELS) as TabName[]).filter((tab) => canShowTab(tab)).flatMap((tab) => {
                  // Dashboard steps aside as "active" while reached via the Collectoren shortcut.
                  const isActive = tab === 'dashboard' ? activeTab === 'dashboard' && !collectorenActive : activeTab === tab;
                  const pills = [
                    <li key={tab}>
                      <button
                        type="button"
                        ref={isActive ? scrollActivePillIntoView : undefined}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => goToTab(tab)}
                        className={`px-3 py-2 text-sm font-semibold rounded-lg whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-td-blue text-white'
                            : 'text-td-navy bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {TAB_LABELS[tab]}
                      </button>
                    </li>,
                  ];
                  // Familiar "Collectoren" shortcut, right after Dashboard.
                  if (tab === 'dashboard' && canShowTab('dashboard')) {
                    const cActive = collectorenActive && activeTab === 'dashboard';
                    pills.push(
                      <li key="collectoren-link">
                        <button
                          type="button"
                          ref={cActive ? scrollActivePillIntoView : undefined}
                          aria-current={cActive ? 'page' : undefined}
                          onClick={goToCollectoren}
                          className={`px-3 py-2 text-sm font-semibold rounded-lg whitespace-nowrap transition-colors ${
                            cActive
                              ? 'bg-td-blue text-white'
                              : 'text-td-navy bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          Collectoren
                        </button>
                      </li>,
                    );
                  }
                  return pills;
                })}
              </ul>
            </nav>
          </div>

          {/* Two-column layout */}
          <div className="flex gap-6">
            {/* Left sidebar - desktop only */}
            <div className="hidden lg:block w-[250px] shrink-0">
              <ProjectSidebar
                detail={detail}
                activeTab={activeTab}
                onTabChange={goToTab}
                isTmProject={isTmProject}
                showCollectorenLink={canShowTab('dashboard')}
                onCollectorenShortcut={goToCollectoren}
                collectorenActive={collectorenActive && activeTab === 'dashboard'}
              />
            </div>

            {/* Main content — only render if user has permission for active tab */}
            <div className="flex-1 min-w-0">
              {canShowTab(activeTab) && (
                <>
                  {activeTab === 'installateur_taak' && !taakOnDashboard && detail && (
                    <InstallateurTaakTab projectId={detail.projectId} projectName={detail.projectName} detail={detail} />
                  )}
                  {activeTab === 'offerte' && (
                    <OfferteTab
                      saleOrders={detail.saleOrders ?? []}
                      saleOrder={detail.saleOrder}
                      lines={detail.saleOrderLines}
                      partnerWarning={detail.partnerWarning}
                    />
                  )}
                  {activeTab === 'planning' && (
                    <PlanningTab
                      projectId={detail.projectId}
                      projectTasks={detail.projectTasks}
                      currentTaskId={activeTaskId}
                      installerNames={detail.installerNames}
                    />
                  )}
                  {activeTab === 'materiaal' && detail && (
                    <MateriaalTab projectId={detail.projectId} projectTasks={detail.projectTasks} isEasykit={isEasykit} />
                  )}
                  {activeTab === 'documenten' && detail && (
                    <DocumentenTab projectId={detail.projectId} projectName={detail.projectName} />
                  )}
                  {activeTab === 'inkooporders' && detail && (
                    <InkoopordersTab projectId={detail.projectId} projectName={detail.projectName} />
                  )}
                  {activeTab === 'dashboard' && detail && (
                    <DashboardTab
                      detail={detail}
                      focusSection={focusDashboardSection}
                      onFocusConsumed={() => setFocusDashboardSection(null)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {koppelModalOpen && detail && (
        <KoppelVerkoopsorderModal
          projectId={detail.projectId}
          projectName={detail.projectName}
          reinvoicedSaleOrder={detail.reinvoiced_sale_order_id}
          onClose={() => setKoppelModalOpen(false)}
          onSuccess={(targetProjectId) => {
            setKoppelModalOpen(false);
            navigate(`/project/${targetProjectId}`);
          }}
        />
      )}
    </div>
  );
}
